/**
 * Feedback Responses Tab - Admin View
 * Primary owner can view ALL customer feedback and respond
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  MessageCircle, Send, ThumbsUp, Lightbulb, AlertCircle, 
  HelpCircle, Search, RefreshCw, Reply, CheckCircle, Clock,
  Building2, User, Filter, Printer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEEDBACK_TYPES = [
  { value: "compliment", label: "Compliment", icon: ThumbsUp, color: "text-green-600", bgColor: "bg-green-50" },
  { value: "suggestion", label: "Suggestion", icon: Lightbulb, color: "text-amber-600", bgColor: "bg-amber-50" },
  { value: "issue", label: "Issue", icon: AlertCircle, color: "text-red-600", bgColor: "bg-red-50" },
  { value: "question", label: "Question", icon: HelpCircle, color: "text-blue-600", bgColor: "bg-blue-50" },
  { value: "other", label: "Other", icon: MessageCircle, color: "text-purple-600", bgColor: "bg-purple-50" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "reviewed", label: "Reviewed", color: "bg-amber-100 text-amber-700" },
  { value: "responded", label: "Responded", color: "bg-green-100 text-green-700" },
  { value: "resolved", label: "Resolved", color: "bg-gray-100 text-gray-600" },
];

const PRIORITY_COLORS = {
  low: "bg-gray-100 text-gray-600",
  normal: "bg-blue-100 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-600",
};

export const FeedbackResponsesTab = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  
  // Response dialog
  const [respondDialog, setRespondDialog] = useState({ open: false, item: null });
  const [responseText, setResponseText] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAllFeedback();
  }, []);

  useEffect(() => {
    filterFeedback();
  }, [feedbackList, searchTerm, statusFilter, typeFilter]);

  const fetchAllFeedback = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/feedback/admin/all`);
      setFeedbackList(res.data.feedback || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const filterFeedback = () => {
    let filtered = [...feedbackList];
    
    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.subject?.toLowerCase().includes(term) ||
        item.message?.toLowerCase().includes(term) ||
        item.userEmail?.toLowerCase().includes(term) ||
        item.userName?.toLowerCase().includes(term) ||
        item.organizationName?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }
    
    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(item => item.feedbackType === typeFilter);
    }
    
    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    setFilteredList(filtered);
  };

  const openRespondDialog = (item) => {
    setRespondDialog({ open: true, item });
    setResponseText(item.adminResponse || "");
    setNewStatus(item.status || "reviewed");
    setSendEmail(false);
  };

  const handleSubmitResponse = async () => {
    if (!respondDialog.item) return;
    
    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/feedback/admin/${respondDialog.item.id}/respond`, {
        response: responseText,
        status: newStatus,
        sendEmail: sendEmail
      });
      
      if (response.data.emailSent) {
        toast.success("Response saved and email sent to customer!");
      } else {
        toast.success("Response saved successfully");
      }
      setRespondDialog({ open: false, item: null });
      setResponseText("");
      fetchAllFeedback();
    } catch (error) {
      console.error("Error saving response:", error);
      toast.error("Failed to save response");
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeInfo = (type) => FEEDBACK_TYPES.find(t => t.value === type) || FEEDBACK_TYPES[4];
  const getStatusColor = (status) => STATUS_OPTIONS.find(s => s.value === status)?.color || "bg-gray-100 text-gray-600";

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const handlePrintFeedback = () => {
    const generateFeedbackItems = () => {
      return filteredList.map(item => {
        const typeInfo = getTypeInfo(item.feedbackType);
        const statusClass = item.status === 'new' ? 'badge-new' : item.status === 'reviewed' ? 'badge-reviewed' : 'badge-responded';
        const userName = item.userName || 'Anonymous';
        const orgName = item.organizationName || 'N/A';
        const dateStr = formatDate(item.createdAt);
        const subject = item.subject || 'No Subject';
        const message = item.message || '';
        const adminResponseHtml = item.adminResponse 
          ? `<div class="admin-response"><div class="admin-response-label">Admin Response:</div>${item.adminResponse}</div>` 
          : '';
        
        return `<div class="feedback-item">
          <div class="feedback-header">
            <div>
              <span class="feedback-type">${typeInfo.label}</span>
              <span class="badge ${statusClass}" style="margin-left: 8px;">${item.status || 'new'}</span>
            </div>
            <div class="feedback-meta">${userName} • ${orgName} • ${dateStr}</div>
          </div>
          <div class="feedback-subject">${subject}</div>
          <div class="feedback-message">${message}</div>
          ${adminResponseHtml}
        </div>`;
      }).join('');
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Customer Feedback Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a365d; border-bottom: 2px solid #3182ce; padding-bottom: 10px; }
          .stats { display: flex; gap: 30px; margin-bottom: 20px; }
          .stat { background: #f7fafc; padding: 15px; border-radius: 8px; }
          .stat-label { font-size: 12px; color: #718096; }
          .stat-value { font-size: 24px; font-weight: bold; color: #2d3748; }
          .feedback-item { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 16px; page-break-inside: avoid; }
          .feedback-header { display: flex; justify-content: space-between; margin-bottom: 12px; border-bottom: 1px solid #edf2f7; padding-bottom: 8px; }
          .feedback-type { font-weight: 600; color: #4a5568; }
          .feedback-meta { font-size: 12px; color: #718096; }
          .feedback-subject { font-weight: 600; font-size: 16px; margin-bottom: 8px; }
          .feedback-message { color: #4a5568; line-height: 1.6; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
          .badge-new { background: #bee3f8; color: #2b6cb0; }
          .badge-reviewed { background: #fefcbf; color: #975a16; }
          .badge-responded { background: #c6f6d5; color: #276749; }
          .admin-response { background: #f0fff4; border-left: 3px solid #48bb78; padding: 12px; margin-top: 12px; }
          .admin-response-label { font-size: 12px; color: #276749; font-weight: 600; margin-bottom: 4px; }
          .print-date { text-align: right; color: #718096; font-size: 12px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Customer Feedback Report</h1>
        <p class="print-date">Generated: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Total Feedback</div><div class="stat-value">${feedbackList.length}</div></div>
          <div class="stat"><div class="stat-label">New</div><div class="stat-value" style="color: #2b6cb0;">${newCount}</div></div>
          <div class="stat"><div class="stat-label">Pending</div><div class="stat-value" style="color: #975a16;">${pendingCount}</div></div>
          <div class="stat"><div class="stat-label">Responded</div><div class="stat-value" style="color: #276749;">${respondedCount}</div></div>
        </div>
        ${generateFeedbackItems()}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Stats
  const newCount = feedbackList.filter(f => f.status === "new").length;
  const pendingCount = feedbackList.filter(f => f.status === "reviewed").length;
  const respondedCount = feedbackList.filter(f => f.status === "responded" || f.status === "resolved").length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{feedbackList.length}</p>
                <p className="text-sm text-blue-600">Total Feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{newCount}</p>
                <p className="text-sm text-amber-600">New (Unread)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{pendingCount}</p>
                <p className="text-sm text-purple-600">Pending Response</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{respondedCount}</p>
                <p className="text-sm text-green-600">Responded</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border border-gray-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by subject, message, user, or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FEEDBACK_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAllFeedback} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              onClick={handlePrintFeedback}
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              disabled={filteredList.length === 0}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <Card className="border border-gray-200">
        <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Customer Feedback ({filteredList.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading feedback...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No feedback found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredList.map((item) => {
                const typeInfo = getTypeInfo(item.feedbackType);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div 
                    key={item.id} 
                    className={`border-2 rounded-xl p-4 transition-all hover:shadow-md ${
                      item.status === 'new' ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${typeInfo.bgColor}`}>
                          <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">{item.subject}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <User className="w-3 h-3" />
                            <span>{item.userName || item.userEmail}</span>
                            <span className="text-gray-300">|</span>
                            <Building2 className="w-3 h-3" />
                            <span>{item.organizationName || 'Unknown'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.priority && item.priority !== 'normal' && (
                          <Badge className={PRIORITY_COLORS[item.priority]}>
                            {item.priority.toUpperCase()}
                          </Badge>
                        )}
                        <Badge className={getStatusColor(item.status)}>
                          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    {/* Message */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-gray-700 whitespace-pre-wrap">{item.message}</p>
                    </div>
                    
                    {/* Existing Response */}
                    {item.adminResponse && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <p className="text-xs text-green-700 font-medium mb-1">Your Response:</p>
                        <p className="text-sm text-green-800">{item.adminResponse}</p>
                        {item.respondedAt && (
                          <p className="text-xs text-green-600 mt-2">
                            Responded: {new Date(item.respondedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Action Button */}
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => openRespondDialog(item)}
                        className={item.adminResponse 
                          ? "bg-gray-500 hover:bg-gray-600" 
                          : "bg-indigo-600 hover:bg-indigo-700"
                        }
                      >
                        <Reply className="w-4 h-4 mr-1" />
                        {item.adminResponse ? "Edit Response" : "Respond"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Response Dialog */}
      <AlertDialog open={respondDialog.open} onOpenChange={(open) => setRespondDialog({ ...respondDialog, open })}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-indigo-700">
              <Reply className="w-5 h-5" />
              Respond to Feedback
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-4">
                {respondDialog.item && (
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-800">{respondDialog.item.subject}</span>
                      <Badge className={getStatusColor(respondDialog.item.status)}>
                        {respondDialog.item.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      From: {respondDialog.item.userName} ({respondDialog.item.organizationName})
                    </p>
                    <p className="text-sm text-gray-700 bg-white p-2 rounded border">
                      {respondDialog.item.message}
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Your Response</label>
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response here..."
                    className="min-h-[120px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Update Status</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="responded">Responded</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Email notification option */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-blue-800">
                    <span className="font-medium">Email this response to customer</span>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Send a copy of your response to {respondDialog.item?.userEmail}
                    </p>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitResponse}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {sendEmail ? "Save & Email Response" : "Save Response"}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackResponsesTab;
